// src/pages/ChatWithAI/VerificationDialog.js - CORRIGIDO LINT
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../../contexts/AuthContext';
import { useClientLogContext } from '../../hooks/useClientLogContext';
import { createAxiosWithAuth } from './utils/chatAuthHelper';
import './VerificationDialog.css';

// A verificação é uma chamada longa (thinking + web search); o default da
// instância (30s) não chega
const VERIFICATION_TIMEOUT_MS = 300000;

const VERIFICATION_SYSTEM_PROMPT = `# ROLE E OBJETIVO
Você é um Revisor Jurídico de Elite, especializado e atualizado em todo o ordenamento jurídico de Portugal. A sua única função é realizar uma verificação de qualidade ("pente-fino") na resposta gerada por outro assistente de IA. A sua análise deve ser implacável no rigor, na precisão e na objetividade.

# INPUTS
Você receberá três blocos de informação:
1. **CONTEXTO INICIAL** (se fornecido): Dados ou documentos que foram carregados no início da sessão e que estiveram disponíveis para o LLM durante toda a conversa.
2. **HISTÓRICO DE CONVERSA** (se disponível): As últimas 2 interações (perguntas e respostas) anteriores à resposta sendo avaliada, para contextualizar o diálogo.
3. **PERGUNTA ORIGINAL** do utilizador e a **RESPOSTA INICIAL** do LLM que está sendo avaliada.

Considere todo este contexto na sua análise. O LLM tinha acesso a todas estas informações ao gerar a resposta.
As respostas são geradas por um agente de IA personalizado chamado "JR Robot" do escritório "Jaime Roriz Advogados". Ao usar estes nomes a ia não estará alucinando.

# CHECKLIST DE VERIFICAÇÃO OBRIGATÓRIA (PENTE-FINO)
Siga meticulosamente os seguintes passos para avaliar a 'RESPOSTA INICIAL':

1.  **Análise da Intenção:**
    * Verifique se a 'RESPOSTA INICIAL' corresponde à intenção da 'PERGUNTA ORIGINAL'.
    * A pergunta era uma 'Pergunta Direta' (a pedir um facto, prazo, conceito) ou uma 'Descrição de Caso' (a pedir uma análise estratégica)?
    * A resposta está no formato correto (resposta objetiva vs. análise estruturada de caso)? Se a resposta inventou um cenário para uma pergunta direta, isso é uma falha grave.
    * A resposta considerou adequadamente o contexto inicial e o histórico da conversa?

2.  **Rigor e Precisão Jurídica:**
    * Confirme a exatidão de TODA a informação jurídica. Os artigos de lei, decretos-lei, códigos (Código Civil, NRAU, CT, etc.) e prazos estão corretos e atualizados?
    * A citação da base legal é a mais específica possível? (Ex: Art. 310º, al. g) do CC para rendas, em vez da genérica al. b)).
    * A interpretação da lei está alinhada com a doutrina e a jurisprudência dominantes em Portugal?

3.  **Completude e Relevância:**
    * A resposta aborda todos os pontos, explícitos e implícitos, da pergunta?
    * Existe alguma nuance ou exceção legal importante que foi ignorada e que deveria ter sido mencionada?
    * A resposta contém informação irrelevante ou "palha" que não contribui para o esclarecimento do utilizador?
    * A resposta aproveita adequadamente as informações do contexto inicial fornecido?

4.  **Clareza e Linguagem:**
    * A linguagem é clara, objetiva e adequada para um advogado português? A terminologia jurídica está a ser usada corretamente e sem ambiguidades?

5.  **Fidelidade aos Factos e Contexto (Anti-Alucinação):**
    * A resposta baseia-se estritamente na informação fornecida (contexto inicial, histórico, e pergunta atual)?
    * Foram inventados ou assumidos factos que não constavam no input do utilizador?
    * A resposta mantém coerência com o histórico da conversa?

# FORMATO DA SAÍDA
Após a sua análise, você DEVE responder estritamente no seguinte formato JSON. Não adicione qualquer texto antes ou depois do JSON.

{
  "avaliacao_geral": "...", // Preencher com uma de três opções: "Excelente", "Bom com ressalvas", "Insatisfatório"
  "pontuacao_rigor_juridico": X, // Preencher com uma pontuação de 1 (muito mau) a 10 (perfeito)
  "analise_detalhada": {
    "corresponde_a_intencao": {
      "avaliacao": true/false,
      "comentario": "Justificação breve."
    },
    "precisao_juridica": {
      "avaliacao": true/false,
      "comentario": "Justificação breve. Apontar erros de citação se existirem."
    },
    "completude_da_resposta": {
      "avaliacao": true/false,
      "comentario": "Justificação breve. Apontar omissões se existirem."
    },
    "fidelidade_aos_factos": {
      "avaliacao": true/false,
      "comentario": "Justificação breve. Apontar alucinações se existirem."
    }
  },
  "pontos_de_melhoria": [
    {
      "tipo_de_erro": "...", // Ex: "Citação Legal Imprecisa", "Formato de Resposta Incorreto", "Omissão de Informação"
      "descricao_do_erro": "...", // Ex: "Citou a alínea b) do Art. 310º do CC em vez da mais específica alínea g)."
      "sugestao_de_correcao": "..." // Ex: "Substituir a referência à alínea b) pela alínea g) do Art. 310º do Código Civil."
    }
  ],
  "resposta_corrigida_e_final": "..." // Gerar a versão final e melhorada da resposta, pronta para ser apresentada ao utilizador. Se a resposta inicial estava perfeita, replicá-la aqui.
}`;

const VerificationDialog = ({ 
  isOpen, 
  onClose, 
  userQuestion, 
  llmResponse,
  contextContent = '',
  conversationHistory = [],
  cachedResult = null,
  fromCache = false,
  onVerificationComplete
}) => {
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('summary');
  const [copySuccess, setCopySuccess] = useState(false);

  const isExecutingRef = useRef(false);
  const hasExecutedRef = useRef(false);

  const { refreshAccessToken } = useAuth();
  const { codCli, codProcesso } = useClientLogContext();
  const axiosWithAuth = useMemo(
    () => createAxiosWithAuth(refreshAccessToken),
    [refreshAccessToken]
  );

  // Reset refs quando dialog fecha
  useEffect(() => {
    if (!isOpen) {
      isExecutingRef.current = false;
      hasExecutedRef.current = false;
      setVerificationResult(null);
      setError(null);
      setIsVerifying(false);
    }
  }, [isOpen]);

  // CORRIGIDO: Função de verificação com useCallback
  const performVerification = useCallback(async () => {
    if (isExecutingRef.current || hasExecutedRef.current) {
      console.log('⏭️ Skipping verification - already executed or executing');
      return;
    }

    isExecutingRef.current = true;
    hasExecutedRef.current = true;

    console.log('🔍 Starting verification process...');
    setIsVerifying(true);
    setError(null);
    setVerificationResult(null);

    try {
      // Construir mensagem completa com contexto e histórico
      let fullMessage = '';

      // Adicionar contexto inicial se existir
      if (contextContent && contextContent.trim()) {
        fullMessage += `# CONTEXTO INICIAL DA SESSÃO
${contextContent.trim()}

---

`;
      }

      // Adicionar histórico de conversa se existir
      if (conversationHistory && conversationHistory.length > 0) {
        fullMessage += `# HISTÓRICO DA CONVERSA (últimas ${conversationHistory.length} interações)

`;
        conversationHistory.forEach((msg, idx) => {
          const label = msg.role === 'user' ? 'UTILIZADOR' : 'ASSISTENTE (LLM)';
          fullMessage += `## ${label} (Mensagem ${idx + 1})
${msg.content}

`;
        });
        fullMessage += `---

`;
      }

      // Adicionar pergunta e resposta sendo avaliada
      fullMessage += `# PERGUNTA ORIGINAL DO UTILIZADOR (A SER AVALIADA)
${userQuestion}

# RESPOSTA INICIAL DO LLM (A SER AVALIADA)
${llmResponse}`;

      console.log('📤 Sending request to API...');
      console.log('📝 Context length:', contextContent?.length || 0);
      console.log('📝 History messages:', conversationHistory?.length || 0);
      
      // Fazer chamada ao serviço de chat
      // Sem modelo fixo: o backend resolve o modelo de verificação (AI_FALLBACK_MODEL)
      const response = await axiosWithAuth.post('/api/chatai/generate', {
        messages: [
          {
            role: 'user',
            content: fullMessage
          }
        ],
        systemPrompt: VERIFICATION_SYSTEM_PROMPT,
        groundSearch: true,
        groundSearchMode: 'auto',
        thinkingLevel: 'high',
        codCli,
        codProcesso
      }, {
        timeout: VERIFICATION_TIMEOUT_MS
      });

      console.log('📥 Received response from API');

      if (response.data.success) {
        const content = response.data.data.content;
        console.log('📝 Content length:', content.length);
        
        // Extrair JSON da resposta
        let jsonStr = content;
        const jsonMatch = content.match(/```json\n([\s\S]*?)\n```/);
        if (jsonMatch) {
          jsonStr = jsonMatch[1];
          console.log('✂️ Extracted JSON from code block');
        } else {
          const jsonObjMatch = content.match(/\{[\s\S]*\}/);
          if (jsonObjMatch) {
            jsonStr = jsonObjMatch[0];
            console.log('✂️ Extracted JSON object');
          }
        }

        let finalResult;
        try {
          const verificationData = JSON.parse(jsonStr);
          console.log('✅ Successfully parsed JSON');
          finalResult = verificationData;
        } catch (parseError) {
          console.error('❌ Error parsing verification JSON:', parseError);
          console.log('Raw content preview:', content.substring(0, 200));
          finalResult = {
            raw_response: content,
            parse_error: true
          };
        }

        console.log('💾 Setting verification result');
        setVerificationResult(finalResult);
        
        if (onVerificationComplete) {
          console.log('📢 Notifying parent component');
          onVerificationComplete(finalResult);
        }
      } else {
        console.error('❌ API returned unsuccessful response');
        throw new Error('Resposta da API não foi bem-sucedida');
      }
    } catch (err) {
      console.error('❌ Error during verification:', err);
      // O campo `error` traz o detalhe do provider; sem ele fica só a mensagem genérica
      const detail = err.response?.data?.error;
      const base = err.response?.data?.message || err.message || 'Erro ao verificar resposta';
      setError(detail ? `${base}: ${detail}` : base);
    } finally {
      console.log('✓ Verification process complete, setting isVerifying to false');
      setIsVerifying(false);
      isExecutingRef.current = false;
    }
  }, [contextContent, conversationHistory, userQuestion, llmResponse, onVerificationComplete, axiosWithAuth, codCli, codProcesso]);

  // CORRIGIDO: useEffect agora pode incluir performVerification
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (cachedResult) {
      console.log('📦 Loading verification from cache');
      setVerificationResult(cachedResult);
      setIsVerifying(false);
      setError(null);
      hasExecutedRef.current = true;
      return;
    }

    if (!fromCache && userQuestion && llmResponse && !hasExecutedRef.current) {
      console.log('🚀 No cache found, starting new verification');
      performVerification();
    }
  }, [isOpen, cachedResult, fromCache, userQuestion, llmResponse, performVerification]);

  const getScoreColor = (score) => {
    if (score >= 8) return '#27ae60';
    if (score >= 6) return '#f39c12';
    return '#e74c3c';
  };

  const getStatusBadge = (avaliacao) => {
    const badges = {
      'Excelente': { color: '#27ae60', icon: '✓' },
      'Bom com ressalvas': { color: '#f39c12', icon: '⚠' },
      'Insatisfatório': { color: '#e74c3c', icon: '✗' }
    };
    
    const badge = badges[avaliacao] || badges['Bom com ressalvas'];
    
    return (
      <span className="status-badge" style={{ backgroundColor: badge.color }}>
        {badge.icon} {avaliacao}
      </span>
    );
  };

  const handleCopyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      alert('Erro ao copiar para a área de transferência');
    }
  };

  if (!isOpen) return null;

  console.log('🎨 Rendering dialog - isVerifying:', isVerifying, 'hasResult:', !!verificationResult, 'hasError:', !!error);

  return (
    <div className="verification-dialog-overlay" onClick={onClose}>
      <div className="verification-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="verification-dialog-header">
          <div className="header-title">
            <h2>🔍 Verificação de Resposta</h2>
            {fromCache && (
              <span className="cache-badge" title="Resultado carregado do cache">
                💾 Cache
              </span>
            )}
            <div className="context-indicators">
              {contextContent && (
                <span className="context-badge" title="Com contexto inicial">
                  📄 Contexto
                </span>
              )}
              {conversationHistory && conversationHistory.length > 0 && (
                <span className="history-badge" title={`${conversationHistory.length} mensagens de histórico`}>
                  💬 Histórico ({conversationHistory.length})
                </span>
              )}
            </div>
          </div>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="verification-dialog-content">
          {isVerifying && (
            <div className="verification-loading">
              <div className="loading-spinner"></div>
              <p>Aguardando verificação...</p>
              <span className="loading-subtitle">
                Analisando rigor jurídico com IA especializada
                {contextContent && ' • Incluindo contexto'}
                {conversationHistory?.length > 0 && ` • Analisando ${conversationHistory.length} mensagens anteriores`}
              </span>
            </div>
          )}

          {error && !isVerifying && (
            <div className="verification-error">
              <span className="error-icon">⚠️</span>
              <div className="error-content">
                <h3>Erro na Verificação</h3>
                <p>{error}</p>
              </div>
              <button className="retry-btn" onClick={() => {
                hasExecutedRef.current = false;
                performVerification();
              }}>
                🔄 Tentar Novamente
              </button>
            </div>
          )}

          {!isVerifying && !error && verificationResult && (
            <>
              {verificationResult.parse_error ? (
                <div className="verification-raw">
                  <h3>⚠️ Resposta recebida (formato não estruturado)</h3>
                  <p className="parse-error-note">
                    A resposta da IA não pôde ser parseada como JSON estruturado. 
                    O conteúdo bruto é exibido abaixo:
                  </p>
                  <pre>{verificationResult.raw_response}</pre>
                  <button 
                    className="copy-btn"
                    onClick={() => handleCopyToClipboard(verificationResult.raw_response)}
                  >
                    {copySuccess ? '✓ Copiado!' : '📋 Copiar Resposta Bruta'}
                  </button>
                </div>
              ) : (
                <>
                  <div className="verification-tabs">
                    <button
                      className={`tab-btn ${activeTab === 'summary' ? 'active' : ''}`}
                      onClick={() => setActiveTab('summary')}
                    >
                      📊 Resumo
                    </button>
                    <button
                      className={`tab-btn ${activeTab === 'details' ? 'active' : ''}`}
                      onClick={() => setActiveTab('details')}
                    >
                      🔍 Análise Detalhada
                    </button>
                    <button
                      className={`tab-btn ${activeTab === 'improved' ? 'active' : ''}`}
                      onClick={() => setActiveTab('improved')}
                    >
                      ✨ Resposta Melhorada
                    </button>
                  </div>

                  <div className="verification-tab-content">
                    {activeTab === 'summary' && (
                      <div className="summary-tab">
                        <div className="summary-header">
                          {getStatusBadge(verificationResult.avaliacao_geral)}
                          <div className="score-display">
                            <span className="score-label">Rigor Jurídico</span>
                            <span 
                              className="score-value" 
                              style={{ color: getScoreColor(verificationResult.pontuacao_rigor_juridico) }}
                            >
                              {verificationResult.pontuacao_rigor_juridico}/10
                            </span>
                          </div>
                        </div>

                        <div className="summary-metrics">
                          <div className="metric-card">
                            <span className="metric-icon">
                              {verificationResult.analise_detalhada.corresponde_a_intencao.avaliacao ? '✓' : '✗'}
                            </span>
                            <div className="metric-info">
                              <h4>Intenção</h4>
                              <p>{verificationResult.analise_detalhada.corresponde_a_intencao.avaliacao ? 'Adequada' : 'Inadequada'}</p>
                            </div>
                          </div>

                          <div className="metric-card">
                            <span className="metric-icon">
                              {verificationResult.analise_detalhada.precisao_juridica.avaliacao ? '✓' : '✗'}
                            </span>
                            <div className="metric-info">
                              <h4>Precisão Jurídica</h4>
                              <p>{verificationResult.analise_detalhada.precisao_juridica.avaliacao ? 'Precisa' : 'Imprecisa'}</p>
                            </div>
                          </div>

                          <div className="metric-card">
                            <span className="metric-icon">
                              {verificationResult.analise_detalhada.completude_da_resposta.avaliacao ? '✓' : '✗'}
                            </span>
                            <div className="metric-info">
                              <h4>Completude</h4>
                              <p>{verificationResult.analise_detalhada.completude_da_resposta.avaliacao ? 'Completa' : 'Incompleta'}</p>
                            </div>
                          </div>

                          <div className="metric-card">
                            <span className="metric-icon">
                              {verificationResult.analise_detalhada.fidelidade_aos_factos.avaliacao ? '✓' : '✗'}
                            </span>
                            <div className="metric-info">
                              <h4>Fidelidade</h4>
                              <p>{verificationResult.analise_detalhada.fidelidade_aos_factos.avaliacao ? 'Fiel aos factos' : 'Alucinações detectadas'}</p>
                            </div>
                          </div>
                        </div>

                        {verificationResult.pontos_de_melhoria && verificationResult.pontos_de_melhoria.length > 0 && (
                          <div className="improvements-summary">
                            <h3>⚠️ Pontos de Melhoria ({verificationResult.pontos_de_melhoria.length})</h3>
                            <ul>
                              {verificationResult.pontos_de_melhoria.map((ponto, index) => (
                                <li key={index}>
                                  <strong>{ponto.tipo_de_erro}</strong>: {ponto.descricao_do_erro}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}

                    {activeTab === 'details' && (
                      <div className="details-tab">
                        <div className="detail-section">
                          <h3>📋 Análise Detalhada</h3>
                          
                          <div className="detail-item">
                            <div className="detail-header">
                              <span className={`detail-status ${verificationResult.analise_detalhada.corresponde_a_intencao.avaliacao ? 'success' : 'error'}`}>
                                {verificationResult.analise_detalhada.corresponde_a_intencao.avaliacao ? '✓' : '✗'}
                              </span>
                              <h4>Correspondência à Intenção</h4>
                            </div>
                            <p>{verificationResult.analise_detalhada.corresponde_a_intencao.comentario}</p>
                          </div>

                          <div className="detail-item">
                            <div className="detail-header">
                              <span className={`detail-status ${verificationResult.analise_detalhada.precisao_juridica.avaliacao ? 'success' : 'error'}`}>
                                {verificationResult.analise_detalhada.precisao_juridica.avaliacao ? '✓' : '✗'}
                              </span>
                              <h4>Precisão Jurídica</h4>
                            </div>
                            <p>{verificationResult.analise_detalhada.precisao_juridica.comentario}</p>
                          </div>

                          <div className="detail-item">
                            <div className="detail-header">
                              <span className={`detail-status ${verificationResult.analise_detalhada.completude_da_resposta.avaliacao ? 'success' : 'error'}`}>
                                {verificationResult.analise_detalhada.completude_da_resposta.avaliacao ? '✓' : '✗'}
                              </span>
                              <h4>Completude da Resposta</h4>
                            </div>
                            <p>{verificationResult.analise_detalhada.completude_da_resposta.comentario}</p>
                          </div>

                          <div className="detail-item">
                            <div className="detail-header">
                              <span className={`detail-status ${verificationResult.analise_detalhada.fidelidade_aos_factos.avaliacao ? 'success' : 'error'}`}>
                                {verificationResult.analise_detalhada.fidelidade_aos_factos.avaliacao ? '✓' : '✗'}
                              </span>
                              <h4>Fidelidade aos Factos</h4>
                            </div>
                            <p>{verificationResult.analise_detalhada.fidelidade_aos_factos.comentario}</p>
                          </div>
                        </div>

                        {verificationResult.pontos_de_melhoria && verificationResult.pontos_de_melhoria.length > 0 && (
                          <div className="detail-section">
                            <h3>🔧 Pontos de Melhoria</h3>
                            {verificationResult.pontos_de_melhoria.map((ponto, index) => (
                              <div key={index} className="improvement-card">
                                <div className="improvement-header">
                                  <span className="improvement-type">{ponto.tipo_de_erro}</span>
                                </div>
                                <div className="improvement-body">
                                  <p><strong>Erro:</strong> {ponto.descricao_do_erro}</p>
                                  <p><strong>Sugestão:</strong> {ponto.sugestao_de_correcao}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {activeTab === 'improved' && (
                      <div className="improved-tab">
                        <div className="improved-header">
                          <h3>✨ Resposta Corrigida e Final</h3>
                          <button 
                            className={`copy-btn ${copySuccess ? 'success' : ''}`}
                            onClick={() => handleCopyToClipboard(verificationResult.resposta_corrigida_e_final)}
                          >
                            {copySuccess ? '✓ Copiado!' : '📋 Copiar'}
                          </button>
                        </div>
                        <div className="improved-content">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {verificationResult.resposta_corrigida_e_final}
                          </ReactMarkdown>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <div className="verification-dialog-footer">
          <button className="close-dialog-btn" onClick={onClose}>
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

export default VerificationDialog;